import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useNavigate } from 'react-router-dom'
import { useCurrency } from '../hooks/useCurrency'
import { decryptField } from '../lib/secureFields'
import { useLanguage } from '../hooks/useLanguage'

// Mapea el idioma de la app a un locale válido para toLocaleDateString
const LOCALE_POR_IDIOMA = {
  es: 'es-CO',
  en: 'en-US',
  fr: 'fr-FR'
}

export default function Calendario() {
  const { t, language } = useLanguage()
  const locale = LOCALE_POR_IDIOMA[language] || 'es-CO'

  const [movimientos, setMovimientos] = useState({})
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)
  const [modalData, setModalData] = useState(null)
  const navigate = useNavigate()
  const [showPicker, setShowPicker] = useState(false)
  const [tempYear, setTempYear] = useState(currentDate.getFullYear())
  const [tempMonth, setTempMonth] = useState(currentDate.getMonth())
  const { format } = useCurrency()

  const MESES = [
    t('cal_enero'), t('cal_febrero'), t('cal_marzo'), t('cal_abril'),
    t('cal_mayo'), t('cal_junio'), t('cal_julio'), t('cal_agosto'),
    t('cal_septiembre'), t('cal_octubre'), t('cal_noviembre'), t('cal_diciembre')
  ]

  const DIAS_SEMANA = [
    t('dia_lun'), t('dia_mar'), t('dia_mie'), t('dia_jue'),
    t('dia_vie'), t('dia_sab'), t('dia_dom')
  ]

async function loadMovimientos() {
  const { data: { session } } = await supabase.auth.getSession()
  const user = session?.user
  if (!user) return

  const año = currentDate.getFullYear()
  const mes = String(currentDate.getMonth() + 1).padStart(2, '0')
  const inicioISO = `${año}-${mes}-01`
  const ultimoDia = new Date(año, currentDate.getMonth() + 1, 0).getDate()
  const finISO = `${año}-${mes}-${String(ultimoDia).padStart(2, '0')}`

  // ── Transacciones y alertas en paralelo ──
  const [
    { data: transactions },
    { data: alerts },
  ] = await Promise.all([
    supabase.from('transactions').select('*').eq('user_id', user.id).gte('date', inicioISO).lte('date', finISO),
    supabase.from('alerts').select('*').eq('user_id', user.id).gte('due_date', inicioISO).lte('due_date', finISO),
  ])

  // ── Descifrar todas las transacciones en paralelo ──
  const transaccionesDescifradas = await Promise.all(
    (transactions || []).map(async (m) => ({
      ...m,
      category: m.category ? await decryptField(m.category) : '',
      description: m.description ? await decryptField(m.description) : ''
    }))
  )

  // ── Agrupar por día ──
  const agrupado = {}

  transaccionesDescifradas.forEach(m => {
    const dia = parseInt(m.date.split('-')[2])
    if (!agrupado[dia]) agrupado[dia] = { ingresos: 0, gastos: 0, alertas: 0, lista: [] }
    agrupado[dia].lista.push(m)
    if (m.type === 'income') agrupado[dia].ingresos += Number(m.amount)
    else agrupado[dia].gastos += Number(m.amount)
  })

  ;(alerts || []).forEach(a => {
    const dia = parseInt(a.due_date.split('-')[2])
    if (!agrupado[dia]) agrupado[dia] = { ingresos: 0, gastos: 0, alertas: 0, lista: [] }
    agrupado[dia].alertas += Number(a.original_amount)
    agrupado[dia].lista.push({ ...a, type: 'alert' })
  })

  setMovimientos(agrupado)
}

  useEffect(() => {
    loadMovimientos()
  }, [currentDate])

  const diasEnMes = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
  0,
  23, 59, 59
  ).getDate()

  const dias = Array.from({ length: diasEnMes }, (_, i) => i + 1)

  const primerDia = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay()

  const espaciosVacios = Array.from({
    length: primerDia === 0 ? 6 : primerDia - 1
  })

  return (
<div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>

  {/* HEADER */}
  <header style={{
    background: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backdropFilter: 'blur(10px)'
  }}>

    <h2
      onClick={() => {
        setTempYear(currentDate.getFullYear())
        setTempMonth(currentDate.getMonth())
        setShowPicker(true)
      }}
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 20,
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        margin: 0,
        textTransform: 'capitalize'
      }}
    >
      📅 {currentDate.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric'
      })}
    </h2>

    <button
      onClick={() => navigate(-1)}
      style={{
        padding: '6px 12px',
        borderRadius: 8,
        border: '1px solid var(--color-border)',
        background: 'transparent',
        fontSize: 12,
        cursor: 'pointer',
        transition: '0.2s',
        color: 'var(--color-text)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.05)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent'
      }}
    >
      {t('volver')}
    </button>

  </header>

  <main style={{
    maxWidth: 900,
    margin: '0 auto',
    padding: 20
  }}>

      {/* DÍAS */}
      <br></br>
            <hr></hr>
      <br></br>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        marginBottom: 8,
        textAlign: 'center',
        fontWeight: 600,
        fontSize: 12,
        color: 'var(--color-muted)'
      }}>
        {DIAS_SEMANA.map((d, i) => (
          <div key={i}>{d}</div>
        ))}
      </div>
      <br></br>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 10
      }}>

        {espaciosVacios.map((_, i) => (
          <div key={'empty-' + i}></div>
        ))}

        {dias.map(dia => {
          const data = movimientos[dia]

          return (
            <div
              key={dia}
              onClick={() => {
                setSelectedDay(dia)
                setModalData(movimientos[dia]?.lista || [])
              }}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 10,
                padding: 8,
                minHeight: 80,
                fontSize: 12,
                cursor: 'pointer',
                transition: '0.2s',
                overflow: 'hidden',
                minWidth: 0
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.03)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)'
              }}
            >
              <div style={{ fontWeight: 600 }}>{dia}</div>

            {data && (
              <div style={{ marginTop: 6 }}>
                {data.ingresos > 0 && (
                  <div style={{ color: '#22c55e', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    +{format(data.ingresos)}
                  </div>
                )}
                {data.gastos > 0 && (
                  <div style={{ color: '#ef4444', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    -{format(data.gastos)}
                  </div>
                )}
                {data.alertas > 0 && (
                  <div style={{ color: '#f97316', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    ⚠{format(data.alertas)}
                  </div>
                )}
              </div>
            )}
            </div>
          )
        })}

        {selectedDay && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
            onClick={() => setSelectedDay(null)}
          >

            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--color-surface)',
                padding: 20,
                borderRadius: 12,
                width: 300,
                maxHeight: 400,
                overflowY: 'auto'
              }}
            >
              <h3 style={{ marginBottom: 10 }}>
                {t('cal_movimientos_dia', { dia: selectedDay })}
              </h3>
              <button
              onClick={() => {
                const fechaSeleccionada = new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth(),
                  selectedDay,
                  0, 0, 0
                )

                const fechaLocal = fechaSeleccionada.toLocaleDateString('en-CA')

                navigate('/ingresos', {
                  state: { fecha: fechaLocal }
                })
              }}              
              style={{
                width: '100%',
                marginBottom: 10,
                padding: 8,
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: '#22c55e', // verde
                color: '#fff',              // texto blanco
                border: 'none'
              }}
            >
              {t('cal_agregar_ingreso')}
            </button>
            <button
              onClick={() => {
                const fechaSeleccionada = new Date(
                  currentDate.getFullYear(),
                  currentDate.getMonth(),
                  selectedDay,
                  0, 0, 0
                )

                const fechaLocal = fechaSeleccionada.toLocaleDateString('en-CA')

                navigate('/gastos', {
                  state: { fecha: fechaLocal }
                })
              }}              
              style={{
                width: '100%',
                marginBottom: 10,
                padding: 8,
                borderRadius: 8,
                cursor: 'pointer',
                backgroundColor: '#c52222', // verde
                color: '#fff',              // texto blanco
                border: 'none'
              }}
            >
              {t('cal_agregar_gasto')}
            </button>

              {modalData && modalData.length > 0 ? (
                modalData.map((m, i) => (
                  <div key={i} style={{
                    padding: 8,
                    borderBottom: '1px solid var(--color-border)'
                  }}>
                    <div style={{ fontWeight: 600 }}>
                    {m.type === 'alert'
                      ? ` ${m.title}`
                      : m.category}
                  </div>

                    <div style={{
                        color:
                          m.type === 'income'
                            ? 'green'
                            : m.type === 'expense'
                            ? 'red'
                            : '#f97316'
                      }}>
                      {m.type === 'income' ? '+' : m.type === 'expense' ? '-' : '⚠'}
                      {format(m.amount || m.original_amount)}
                    </div>

                    <div style={{ fontSize: 11, color: 'var(--color-muted)' }}>
                    {m.type === 'alert'
                      ? t('cal_mora_diaria_valor', { pct: m.late_fee_percent })
                      : m.description}
                  </div>
                  </div>
                ))
              ) : (
                <p>{t('cal_no_hay_movimientos')}</p>
              )}

              <button
                onClick={() => setSelectedDay(null)}
                style={{
                  marginTop: 10,
                  width: '100%',
                  padding: 8,
                  borderRadius: 8
                }}
              >
                {t('cerrar')}
              </button>
            </div>

          </div>
        )}
      </div>
      <br></br>
      <hr></hr>
      <br></br>
  {/* 🔥 BOTONES DE MES (AQUÍ ABAJO DE LOS DÍAS) */}
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16
  }}>
  <button
    onClick={() => {
      const newDate = new Date(currentDate)
      newDate.setMonth(currentDate.getMonth() - 1)
      setCurrentDate(newDate)
    }}
    style={{
      fontSize: 20,
      padding: '10px 16px',
      borderRadius: 10,
      cursor: 'pointer',
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      color: 'var(--color-text)'
    }}
  >
    &lt;
  </button>

  <button
    onClick={() => {
      const newDate = new Date(currentDate)
      newDate.setMonth(currentDate.getMonth() + 1)
      setCurrentDate(newDate)
    }}
    style={{
      fontSize: 20,
      padding: '10px 16px',
      borderRadius: 10,
      cursor: 'pointer',
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      color: 'var(--color-text)'
          }}
  >
    &gt;
  </button>
  </div>
  {showPicker && (
    <div
      onClick={() => setShowPicker(false)}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        background: 'var(--color-surface)'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
        background: 'var(--color-surface)',
        padding: 20,
        borderRadius: 12,
        width: 280,
        background: 'var(--color-surface)'
        }}
      >
      <h3 style={{
        marginBottom: 18,
        fontSize: 18,
        fontWeight: 700,
        color: 'var(--color-text)',
        fontFamily: 'var(--font-display)'
      }}>
        {t('cal_seleccionar_fecha')}
      </h3>

      {/* CONTENEDOR */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14
      }}>

        {/* MES */}
        <div>
          <label style={{
            display: 'block',
            marginBottom: 6,
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--color-muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}>
            {t('cal_mes_label')}
          </label>

          <select
            value={tempMonth}
            onChange={(e) => setTempMonth(parseInt(e.target.value))}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontSize: 14,
              outline: 'none',
              transition: '0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              
            }}
            onFocus={(e) => {
              e.target.style.border =
                '1px solid var(--color-primary)'
              e.target.style.boxShadow =
                '0 0 0 3px rgba(34,197,94,0.12)'
            }}
            onBlur={(e) => {
              e.target.style.border =
                '1px solid var(--color-border)'
              e.target.style.boxShadow =
                '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            {MESES.map((m, i) => (
              <option key={i} value={i}>
                {m}
              </option>
            ))}
          </select>
        </div>

        {/* AÑO */}
        <div>
          <label style={{
            display: 'block',
            marginBottom: 6,
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--color-muted)',
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}>
            {t('cal_anio_label')}
          </label>

          <input
            type="number"
            value={tempYear}
            onChange={(e) =>
              setTempYear(parseInt(e.target.value))
            }
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontSize: 14,
              outline: 'none',
              transition: '0.2s',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
            onFocus={(e) => {
              e.target.style.border =
                '1px solid var(--color-primary)'
              e.target.style.boxShadow =
                '0 0 0 3px rgba(34,197,94,0.12)'
            }}
            onBlur={(e) => {
              e.target.style.border =
                '1px solid var(--color-border)'
              e.target.style.boxShadow =
                '0 2px 8px rgba(0,0,0,0.06)'
            }}
          />
        </div>

        {/* BOTONES */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginTop: 10
        }}>
          <button
            onClick={() => {
              setCurrentDate(
                new Date(tempYear, tempMonth, 1)
              )
              setShowPicker(false)
            }}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: 14,
              border: 'none',
              background: 'var(--color-primary)',
              color: 'white',
              fontWeight: 700,
              cursor: 'pointer',
              transition: '0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            {t('cal_aplicar')}
          </button>

          <button
            onClick={() => setShowPicker(false)}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: 14,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontWeight: 600,
              cursor: 'pointer',
              transition: '0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                'rgba(255,255,255,0.04)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                'var(--color-surface)'
            }}
          >
            {t('cancelar')}
          </button>
        </div>
      </div>
      </div>
    </div>
  )}
</main>
</div>
)
}